// Guardian and delegate QR passes, verified offline by the kiosk. See docs/spec/events.md §6.
import { b64url, fromB64url, utf8 } from "./bytes.ts";
import { canonicalBytes, canonicalHash, canonicalize } from "./canonical.ts";
import { signBytes, signCanonical, verifyBytes, verifyCanonical } from "./keys.ts";

export interface Pass {
  v: 1;
  kind: "pass";
  site_id: string;
  adult_id: string;
  child_ids: string[];
  not_after: number; // unix seconds
  holder_key: string | null; // app public key; null for a static delegate pass
  delegation_id: string | null;
}

export const WINDOW_SECONDS = 30;
export const windowOf = (unixSeconds: number) => Math.floor(unixSeconds / WINDOW_SECONDS);

/** Site side: signs a pass. The site key never leaves the Worker. */
export async function issuePass(siteKey: CryptoKey, pass: Pass): Promise<string> {
  const body = b64url(canonicalBytes(pass));
  return `${body}.${await signBytes(siteKey, utf8(body))}`;
}

/** Holder side (guardian app): renders the QR payload for the current 30 s window. */
export async function presentPass(signedPass: string, holderKey: CryptoKey | null, nowSeconds: number): Promise<string> {
  const [body] = signedPass.split(".");
  const w = windowOf(nowSeconds);
  if (!holderKey) return `PMU1.${signedPass}.0.-`;
  const passHash = await canonicalHash(JSON.parse(new TextDecoder().decode(fromB64url(body!))));
  return `PMU1.${signedPass}.${w}.${await signCanonical(holderKey, { pass_hash: passHash, window: w })}`;
}

export type PassCheck = { ok: true; pass: Pass; static: boolean } | { ok: false; reason: string };

/** Kiosk side: verifies site signature, expiry, window freshness and the holder signature. */
export async function verifyPresented(qr: string, sitePublicKey: string, siteId: string, nowSeconds: number): Promise<PassCheck> {
  const parts = qr.split(".");
  if (parts.length !== 5 || parts[0] !== "PMU1") return { ok: false, reason: "not a Vavr pass" };
  const [, body, siteSig, win, holderSig] = parts as [string, string, string, string, string];
  if (!(await verifyBytes(sitePublicKey, utf8(body), siteSig))) return { ok: false, reason: "site signature invalid" };
  let pass: Pass;
  try {
    pass = JSON.parse(new TextDecoder().decode(fromB64url(body)));
    if (canonicalize(pass) !== new TextDecoder().decode(fromB64url(body))) return { ok: false, reason: "pass is not canonical" };
  } catch {
    return { ok: false, reason: "pass unreadable" };
  }
  if (pass.v !== 1 || pass.kind !== "pass") return { ok: false, reason: "unknown pass version" };
  if (pass.site_id !== siteId) return { ok: false, reason: "pass is for another site" };
  if (nowSeconds > pass.not_after) return { ok: false, reason: "pass expired" };
  if (pass.holder_key === null) return { ok: true, pass, static: true };
  const w = Number(win);
  const cur = windowOf(nowSeconds);
  if (!Number.isInteger(w) || (w !== cur && w !== cur - 1)) return { ok: false, reason: "code has rotated; ask them to refresh" };
  const ok = await verifyCanonical(pass.holder_key, { pass_hash: await canonicalHash(pass), window: w }, holderSig);
  return ok ? { ok: true, pass, static: false } : { ok: false, reason: "holder signature invalid" };
}
